import React, { useState, useEffect, useRef } from 'react';
import { Button, DateRangePicker, FilterChip, Search, SearchCombo, Tag, type DateRangeValue } from '@indipia/ui-core';

// ─── Public types ─────────────────────────────────────────────────────────────

export type StatusFilter = 'all' | 'ACTIVE' | 'PROGRESS' | 'DONE';

export interface SearchParams {
  keyword?: string;
  startDate?: string;   // YYYY-MM-DD
  endDate?: string;     // YYYY-MM-DD
  statusFilter?: StatusFilter;
}

export interface StatusCounts {
  total: number;
  ACTIVE: number;
  PROGRESS: number;
  DONE: number;
}

// ─── Internal types ───────────────────────────────────────────────────────────

interface Chip {
  id: string;
  label: string;
}

interface SearchBarProps {
  /** 검색 실행 시 호출 — 부모가 실제 API 함수를 주입한다 */
  onSearch: (params: SearchParams) => void;
  /**
   * 칩 기반 다중 키워드 입력 허용 (검색·공유 화면)
   * true: Enter/버튼으로 칩 추가, 쉼표로 join 후 keyword로 전달
   * false(기본): 단일 텍스트, Enter로 즉시 검색
   */
  enableTagSearch?: boolean;
  /**
   * 상태 필터 (전체·진행중·완료) 노출 여부 (현장 화면)
   * statusCounts와 함께 사용
   */
  enableStatusFilter?: boolean;
  /** enableStatusFilter=true 일 때 각 칩에 표시할 카운트 — 부모 API 응답 기반 */
  statusCounts?: StatusCounts;
  /** 인풋 placeholder — 생략 시 enableTagSearch 여부에 따라 자동 결정 */
  placeholder?: string;
  /** 마운트 시 기본값으로 자동 검색 여부 (기본 false — 사용자 액션 시에만 검색) */
  autoSearchOnMount?: boolean;
  /** 날짜 검색 기준 안내 문구 — 생략 시 "사진의 등록일 기준" */
  dateLabel?: string;
  /** 복원할 초기 검색값 — 상세에서 돌아올 때 UI 상태 복원용 */
  initialValues?: SearchParams;
  /**
   * true: SearchCombo 대신 단순 Search 입력 + 기간 뒤에 [검색] 버튼 (공유 이력 화면).
   * false(기본): 입력과 [검색] 버튼이 붙은 SearchCombo.
   */
  plainSearch?: boolean;
  /** 지정하면 [검색] 뒤에 [초기화](etc) 버튼 노출 — plainSearch 전용 */
  onReset?: () => void;
  /** 검색 입력 바로 오른쪽 같은 줄에 놓을 요소 — 담당자·관리자 조건(D-7 공유·검색) */
  afterSearch?: React.ReactNode;
  /** 상태 칩 오른쪽 같은 줄에 놓을 요소 — 담당자·관리자 조건(D-4 현장 목록) */
  trailing?: React.ReactNode;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { key: StatusFilter; label: string }[] = [
  { key: 'all',      label: '전체' },
  { key: 'ACTIVE',   label: '등록' },
  { key: 'PROGRESS', label: '진행중' },
  { key: 'DONE',     label: '거래완료' },
];

function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SearchBar({
  onSearch,
  enableTagSearch = false,
  enableStatusFilter = false,
  statusCounts,
  placeholder,
  autoSearchOnMount = false,
  dateLabel,
  initialValues,
  plainSearch = false,
  onReset,
  afterSearch,
  trailing,
}: SearchBarProps) {
  const defaultDateRange = (): DateRangeValue => {
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - 3);
    return { start, end };
  };

  const [keyword, setKeyword] = useState('');
  const [chips, setChips] = useState<Chip[]>(() => {
    if (initialValues?.keyword && enableTagSearch) {
      return initialValues.keyword.split(',').filter(Boolean).map((label, i) => ({ id: `restored-${i}`, label }));
    }
    return [];
  });
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => {
    if (initialValues?.startDate || initialValues?.endDate) {
      return {
        start: initialValues.startDate ? new Date(initialValues.startDate + 'T12:00:00') : null,
        end: initialValues.endDate ? new Date(initialValues.endDate + 'T12:00:00') : null,
      };
    }
    return defaultDateRange();
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const initialSearchFired = useRef(false);

  useEffect(() => {
    if (!autoSearchOnMount || initialSearchFired.current) return;
    initialSearchFired.current = true;
    onSearch(buildParams());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolvedPlaceholder = placeholder ??
    (enableTagSearch ? '현장명 · 고객명 · 주소 · 태그 검색' : '현장명 · 고객명 · 주소 검색');

  // React 상태 업데이트는 비동기이므로 최신값을 overrides로 주입
  const buildParams = (overrides: {
    chips?: Chip[];
    plainKeyword?: string;
    dateRange?: DateRangeValue;
    statusFilter?: StatusFilter;
  } = {}): SearchParams => {
    const c = overrides.chips ?? chips;
    const dr = overrides.dateRange ?? dateRange;
    const sf = overrides.statusFilter ?? statusFilter;
    const rawInput = (overrides.plainKeyword ?? keyword).trim();
    const kw = enableTagSearch
      ? (() => {
          const labels = c.map(x => x.label);
          if (rawInput && !c.find(x => x.label === rawInput)) labels.push(rawInput);
          return labels.join(',') || undefined;
        })()
      : (rawInput || undefined);
    return {
      keyword: kw,
      startDate: dr.start ? fmtDate(dr.start) : undefined,
      endDate: dr.end ? fmtDate(dr.end) : undefined,
      ...(enableStatusFilter && { statusFilter: sf }),
    };
  };

  // ── 키워드 제출 ────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    if (enableTagSearch) {
      const trimmed = keyword.trim();
      if (trimmed && !chips.find(c => c.label === trimmed)) {
        const next = [...chips, { id: Date.now().toString(), label: trimmed }];
        setChips(next);
        setKeyword('');
        onSearch(buildParams({ chips: next }));
      } else {
        onSearch(buildParams());
      }
    } else {
      onSearch(buildParams());
    }
  };

  const removeChip = (id: string) => {
    const next = chips.filter(c => c.id !== id);
    setChips(next);
    onSearch(buildParams({ chips: next }));
  };

  const clearAll = () => {
    const next: Chip[] = [];
    setChips(next);
    setKeyword('');
    onSearch(buildParams({ chips: next, plainKeyword: '' }));
  };

  // ── 날짜 변경 ──────────────────────────────────────────────────────────────

  const handleDateChange = (v: DateRangeValue) => {
    setDateRange(v);
    onSearch(buildParams({ dateRange: v }));
  };

  // ── 상태 필터 변경 — API 재호출 없이 부모가 클라이언트 측 필터만 적용 ─────

  const handleStatusChange = (v: StatusFilter) => {
    setStatusFilter(v);
    onSearch(buildParams({ statusFilter: v }));
  };

  const counts: Record<StatusFilter, number> = {
    all:      statusCounts?.total    ?? 0,
    ACTIVE:   statusCounts?.ACTIVE   ?? 0,
    PROGRESS: statusCounts?.PROGRESS ?? 0,
    DONE:     statusCounts?.DONE     ?? 0,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* 첫 번째 행: 검색 조합 + 날짜 + 상태 필터.
          검색 입력과 [검색] 버튼은 표준 조합 SearchCombo(D-09 §15.13)이고, 기간 트리거는 공용
          DateRangePicker(§15.14) — 같은 control-height md 를 쓰되 모서리는 알약이다(필터 성격). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

        {/* 검색 조합 — 버튼 클릭·Enter 공통 트리거 */}
        {plainSearch ? (
          <Search
            className="flex-1 min-w-0"
            value={keyword}
            onChange={v => setKeyword(v.slice(0, 50))}
            onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleSubmit(); }}
            onClear={() => { if (!enableTagSearch) onSearch(buildParams({ plainKeyword: '' })); }}
            maxLength={50}
            placeholder={resolvedPlaceholder}
            role="searchbox"
          />
        ) : (
          <SearchCombo
            className="flex-1 min-w-0"
            value={keyword}
            onChange={v => setKeyword(v.slice(0, 50))}
            onSearch={handleSubmit}
            onClear={() => { if (!enableTagSearch) onSearch(buildParams({ plainKeyword: '' })); }}
            maxLength={50}
            placeholder={resolvedPlaceholder}
            role="searchbox"
          />
        )}

        {afterSearch}

        {/* 날짜 범위 */}
        <DateRangePicker value={dateRange} onChange={handleDateChange} />

        {/* 단순 검색형 — 기간 뒤에 검색 · 초기화 */}
        {plainSearch && (
          <>
            <Button variant="primary" onClick={handleSubmit} style={{ flexShrink: 0 }}>검색</Button>
            {onReset && <Button variant="etc" onClick={onReset} style={{ flexShrink: 0 }}>초기화</Button>}
          </>
        )}

        {/* 상태 필터 — enableStatusFilter=true 일 때만 */}
        {enableStatusFilter && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {STATUS_OPTIONS.map(({ key, label }) => (
              <FilterChip
                key={key}
                active={statusFilter === key}
                aria-pressed={statusFilter === key}
                count={counts[key]}
                onClick={() => handleStatusChange(key)}
              >
                {label}
              </FilterChip>
            ))}
          </div>
        )}

        {trailing}

      </div>

      {/* 날짜 안내 문구 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span style={{ fontSize: 12, color: 'var(--shell-hint)' }}>{dateLabel ?? '날짜 검색 시 사진의 등록일 기준으로 검색됩니다.'}</span>
      </div>

      {/* 칩 목록 — enableTagSearch이고 칩이 있을 때만 */}
      {enableTagSearch && chips.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface-2)', flexShrink: 0 }}>
            적용된 필터:
          </span>
          {chips.map(chip => (
            <Tag key={chip.id} onRemove={() => removeChip(chip.id)}>
              {chip.label}
            </Tag>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            style={{ color: 'var(--color-error)' }}
          >
            모두 지우기
          </Button>
        </div>
      )}

    </div>
  );
}
